import { identifyUser, resetUser, trackAnalyticsEvent } from '@/integrations/analytics';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../api/authService';
import type { ChallengeResponse, LoginCredentials, User } from '../types/auth';

interface AuthStore {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  pendingChallenge: { session: string; username: string } | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  clearError: () => void;
  clearChallenge: () => void;
  checkAuth: () => Promise<void>;
  refreshUser: () => Promise<void>;
  submitNewPassword: (newPassword: string) => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      pendingChallenge: null,

      login: async (credentials: LoginCredentials) => {
        set({ isLoading: true, error: null, pendingChallenge: null });
        try {
          const response = await authService.login(credentials);
          const data = response.data;

          if ('ChallengeName' in data && data.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
            set({
              isLoading: false,
              pendingChallenge: {
                session: (data as ChallengeResponse).Session,
                username: credentials.username,
              },
            });
            return;
          }

          const user = data as User;
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          try {
            const analyticsUserId = user.email || user.username || user.id;
            if (analyticsUserId) {
              identifyUser(analyticsUserId, {
                username: user.username,
                email: user.email,
                fullName: user.fullName,
                firstName: user.firstName,
                lastName: user.lastName,
                role: user.roles?.[0],
                roles: user.roles,
                client: user.client,
                subcontractor: user.subcontractor,
              });
            }
            trackAnalyticsEvent('login_success', { method: 'password' });
          } catch (analyticsError) {
            console.error('Analytics identification failed:', analyticsError);
          }
        } catch (error) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Login failed',
          });
          throw error;
        }
      },

      logout: async () => {
        set({ isLoading: true });
        try {
          await authService.logout();

          // Reset analytics user
          try {
            resetUser();
            trackAnalyticsEvent('logout');
          } catch (analyticsError) {
            console.error('Analytics reset failed:', analyticsError);
          }
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          });
        }
      },

      setUser: (user: User | null) => {
        set({
          user,
          isAuthenticated: !!user,
        });
      },

      clearError: () => {
        set({ error: null });
      },

      clearChallenge: () => {
        set({ pendingChallenge: null });
      },

      submitNewPassword: async (newPassword: string) => {
        const { pendingChallenge } = get();
        if (!pendingChallenge) throw new Error('No pending challenge');

        set({ isLoading: true, error: null });
        try {
          await authService.handleNewPasswordChallenge({
            username: pendingChallenge.username,
            password: newPassword,
            session: pendingChallenge.session,
          });
          set({ pendingChallenge: null, isLoading: false });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to set new password',
          });
          throw error;
        }
      },

      checkAuth: async () => {
        const { user } = get();
        if (!user) {
          set({ isAuthenticated: false });
          return;
        }

        set({ isLoading: true });
        try {
          // Verify the current session is still valid
          await authService.isAuthenticated();
          set({ isAuthenticated: true, isLoading: false });
        } catch (_error) {
          // Session is invalid, clear user
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      refreshUser: async () => {
        set({ isLoading: true });
        try {
          const response = await authService.getCurrentUser();
          const user = response.data;
          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });

          // Update analytics identity after refresh
          try {
            const analyticsUserId = user.email || user.username || user.id;
            if (analyticsUserId) {
              identifyUser(analyticsUserId, {
                username: user.username,
                email: user.email,
                fullName: user.fullName,
                firstName: user.firstName,
                lastName: user.lastName,
                role: user.roles?.[0],
                roles: user.roles,
                client: user.client,
                subcontractor: user.subcontractor,
              });
            }
          } catch (analyticsError) {
            console.error('Analytics identification failed:', analyticsError);
          }
        } catch (error) {
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to refresh user',
          });
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
